import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { authService } from '../services/authService';
import { setUser, setToken } from '../store/slices/authSlice';
import { FieldError, inputErrorClass } from '../components/FieldError';
import { BackArrowIcon } from '../components/BackArrowIcon';

export function SignUpPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState<'email' | 'code' | 'form'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    company: '',
    designation: '',
    mobile: '',
  });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const normalizedEmail = email.trim().toLowerCase();

  const sendCode = async () => {
    setErrors({});
    if (!normalizedEmail) {
      setErrors({ email: 'Work email is required.' });
      return;
    }
    setLoading(true);
    try {
      await authService.sendSignupCode(normalizedEmail);
      // New code invalidates any previous one in the DB — clear local digits so we never submit an old code.
      setCode('');
      setStep('code');
    } catch (e: unknown) {
      setErrors({ submit: e instanceof Error ? e.message : 'Failed' });
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setErrors({});
    if (!code.trim() || code.trim().length !== 6) {
      setErrors({ code: 'Enter the 6-digit code from your email.' });
      return;
    }
    setLoading(true);
    try {
      await authService.verifySignupCode(normalizedEmail, code.trim());
      setStep('form');
    } catch (e: unknown) {
      setErrors({ code: e instanceof Error ? e.message : 'Verification failed' });
    } finally {
      setLoading(false);
    }
  };

  const signUp = async () => {
    setErrors({});
    const e: Record<string, string> = {};
    if (!formData.name.trim()) e.name = 'Full name is required.';
    if (!formData.designation.trim()) e.designation = 'Designation is required.';
    if (!formData.mobile.trim()) e.mobile = 'Mobile number is required.';
    if (!formData.company.trim()) e.company = 'Company is required.';
    if (!formData.password) e.password = 'Password is required.';
    if (!agree) e.terms = 'Please accept the Terms & Conditions.';
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setLoading(true);
    try {
      const res = await authService.signUp({
        ...formData,
        email: normalizedEmail,
        code: code.trim(),
      });
      dispatch(setUser(res.user));
      dispatch(setToken(res.token));
      navigate('/home');
    } catch (err: unknown) {
      setErrors({ submit: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-6 py-8 max-w-lg mx-auto">
      <Link
        to="/signin"
        className="mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500"
      >
        <BackArrowIcon />
        Back
      </Link>
      <h1 className="text-3xl font-black text-slate-900 mb-2">Create Account</h1>
      {step === 'form' && (
        <>
          <p className="text-emerald-700 text-sm font-medium mb-1">Email verified. Complete your profile below.</p>
          <p className="text-slate-600 text-sm font-medium mb-6">* All fields are mandatory</p>
        </>
      )}

      {step === 'email' && (
        <>
          <label className="block text-[10px] font-black text-blue-600 uppercase mb-2">Work Email</label>
          <input
            className={`w-full rounded-2xl bg-slate-100 px-4 py-3 mb-1 ${errors.email ? inputErrorClass : ''}`}
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            type="email"
            autoComplete="email"
          />
          <FieldError message={errors.email} />
          <FieldError message={errors.submit} />
          <button
            type="button"
            disabled={loading}
            onClick={sendCode}
            className="w-full py-5 rounded-2xl bg-blue-600 text-white font-black mt-6 disabled:opacity-50"
          >
            {loading ? '…' : 'Send verification code'}
          </button>
        </>
      )}

      {step === 'code' && (
        <>
          <label className="block text-[10px] font-black text-blue-600 uppercase mb-2">Verification code</label>
          <input
            className={`w-full rounded-2xl bg-slate-100 px-4 py-3 mb-1 ${errors.code ? inputErrorClass : ''}`}
            value={code}
            onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
          />
          <FieldError message={errors.code} />
          <FieldError message={errors.submit} />
          <button
            type="button"
            disabled={loading}
            onClick={() => void verifyCode()}
            className="w-full py-5 rounded-2xl bg-blue-600 text-white font-black mt-6 disabled:opacity-50"
          >
            {loading ? 'Verifying…' : 'Verify email'}
          </button>
          <div className="flex flex-col gap-3 items-center mt-4">
            <button
              type="button"
              disabled={loading}
              onClick={sendCode}
              className="text-blue-600 text-sm font-semibold disabled:opacity-50"
            >
              {loading ? 'Sending…' : 'Resend verification code'}
            </button>
            <button type="button" onClick={() => setStep('email')} className="text-blue-400 text-sm">
              Use a different email
            </button>
          </div>
        </>
      )}

      {step === 'form' && (
        <>
          <label className="block text-[10px] font-black text-blue-600 uppercase mb-2">Work Email</label>
          <input className="w-full rounded-2xl bg-slate-100 px-4 py-3 text-slate-500 mb-4" readOnly value={normalizedEmail} />

          <label className="block text-[10px] font-black text-blue-600 uppercase mb-2">Full Name</label>
          <input
            className={`w-full rounded-2xl bg-slate-100 px-4 py-3 mb-1 ${errors.name ? inputErrorClass : ''}`}
            value={formData.name}
            onChange={(ev) => {
              setFormData({ ...formData, name: ev.target.value });
              setErrors((o) => {
                const n = { ...o };
                delete n.name;
                return n;
              });
            }}
          />
          <FieldError message={errors.name} />

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <label className="block text-[10px] font-black text-blue-600 uppercase mb-2">Designation</label>
              <input
                className={`w-full rounded-2xl bg-slate-100 px-3 py-3 text-sm mb-1 ${errors.designation ? inputErrorClass : ''}`}
                value={formData.designation}
                onChange={(ev) => {
                  setFormData({ ...formData, designation: ev.target.value });
                  setErrors((o) => {
                    const n = { ...o };
                    delete n.designation;
                    return n;
                  });
                }}
              />
              <FieldError message={errors.designation} />
            </div>
            <div>
              <label className="block text-[10px] font-black text-blue-600 uppercase mb-2">Mobile</label>
              <input
                className={`w-full rounded-2xl bg-slate-100 px-3 py-3 text-sm mb-1 ${errors.mobile ? inputErrorClass : ''}`}
                value={formData.mobile}
                onChange={(ev) => {
                  setFormData({ ...formData, mobile: ev.target.value });
                  setErrors((o) => {
                    const n = { ...o };
                    delete n.mobile;
                    return n;
                  });
                }}
              />
              <FieldError message={errors.mobile} />
            </div>
          </div>

          <label className="block text-[10px] font-black text-blue-600 uppercase mb-2 mt-4">Company</label>
          <input
            className={`w-full rounded-2xl bg-slate-100 px-4 py-3 mb-1 ${errors.company ? inputErrorClass : ''}`}
            value={formData.company}
            onChange={(ev) => {
              setFormData({ ...formData, company: ev.target.value });
              setErrors((o) => {
                const n = { ...o };
                delete n.company;
                return n;
              });
            }}
          />
          <FieldError message={errors.company} />

          <label className="block text-[10px] font-black text-blue-600 uppercase mb-2 mt-4">Password</label>
          <input
            type="password"
            className={`w-full rounded-2xl bg-slate-100 px-4 py-3 mb-1 ${errors.password ? inputErrorClass : ''}`}
            value={formData.password}
            onChange={(ev) => {
              setFormData({ ...formData, password: ev.target.value });
              setErrors((o) => {
                const n = { ...o };
                delete n.password;
                return n;
              });
            }}
          />
          <FieldError message={errors.password} />

          <label className="flex gap-3 items-start mt-6 cursor-pointer">
            <button
              type="button"
              onClick={() => {
                setAgree((v) => !v);
                setErrors((o) => {
                  const n = { ...o };
                  delete n.terms;
                  return n;
                });
              }}
              className={`mt-0.5 w-5 h-5 rounded-md border-2 shrink-0 ${agree ? 'bg-blue-600 border-blue-600 text-white' : 'border-blue-600'}`}
            >
              {agree ? '✓' : ''}
            </button>
            <span className="text-sm text-slate-600">
              I have read and agree to the{' '}
              <a
                href={`${typeof window !== 'undefined' ? window.location.origin : ''}/terms`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline font-semibold"
              >
                Terms and Conditions
              </a>
              .
            </span>
          </label>
          <FieldError message={errors.terms} />
          <FieldError message={errors.submit} />

          <button
            type="button"
            disabled={loading || !agree}
            onClick={signUp}
            className="w-full py-5 rounded-2xl bg-blue-600 text-white font-black mt-8 disabled:opacity-50"
          >
            {loading ? '…' : 'Sign Up'}
          </button>
        </>
      )}
    </div>
  );
}
